import React, { useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import { Alert, Snackbar } from '@mui/material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import TopNavbar from 'examples/Navbars/TopNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { useData } from '../../../hooks';
import { apiGetBranches } from '../../../api/branch.api';
import { apiGetGrindHist } from '../../../api/stats.api';
import { useNavigate } from 'react-router-dom';

/**
 * 지점별로 "페트병 분쇄(=grind)" 이력이 있는 유저를 집계해서 보여주는 화면
 * - 왜 집계하나: grindhist는 이벤트(이력) 단위라 유저가 중복되므로, 회원관리 관점에 맞게 userid 기준으로 묶어 보여주기 위함
 * - 성능/안전: 기간 필터를 기본 제공하여 과도한 데이터 조회를 방지
 */
const BranchGrindUsers = () => {
  const navigate = useNavigate();
  const { user, appIsReady } = useData();

  const [branches, setBranches] = useState([]);
  // 'ALL'이면 전체 지점(=branch_id 미필터링)
  const [selectedBranchId, setSelectedBranchId] = useState('ALL');

  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  const [loading, setLoading] = useState(false);
  const [grindRows, setGrindRows] = useState([]);
  const [sumPetCnt, setSumPetCnt] = useState(0);

  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  /**
   * Date -> "YYYY-MM-DD"
   * @param {Date} date
   * @returns {string}
   */
  const toYmd = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  /**
   * grindhist createdAt 표기용 포맷
   * @param {string} dateString
   * @returns {string}
   */
  const formatDateTime = (dateString) => {
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };
    return new Date(dateString)
      .toLocaleString('ko-KR', options)
      .replace(/. /g, '-')
      .replace('.', '')
      .replace(' ', ' ');
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // 기본 기간: 최근 30일
  useEffect(() => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 30);
    setCreatedFrom(toYmd(from));
    setCreatedTo(toYmd(today));
  }, []);

  // 지점 목록 로드 (드롭다운용)
  useEffect(() => {
    const fetchBranches = async () => {
      if (!appIsReady || !user?.accessToken) return;

      try {
        // 드롭다운이므로 넉넉한 limit으로 1페이지 조회
        const response = await apiGetBranches(1, 1000, user.accessToken, '');
        const branchList = Array.isArray(response?.data) ? response.data : [];
        setBranches(branchList);
      } catch (e) {
        console.error('지점 목록 조회 실패:', e);
        setErrorMessage('지점 목록을 불러오지 못했습니다.');
        setOpenSnackbar(true);
      }
    };

    fetchBranches();
  }, [appIsReady, user]);

  /**
   * 선택된 지점/기간으로 grindhist를 조회 후, userid 기준으로 집계한 유저 목록을 구성
   */
  const fetchBranchUsers = async () => {
    if (!user?.accessToken) {
      setErrorMessage('사용자 인증 정보가 없습니다.');
      setOpenSnackbar(true);
      return;
    }

    if (!createdFrom || !createdTo) {
      setErrorMessage('기간(From/To)을 선택해주세요.');
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    try {
      const branchIdParam =
        selectedBranchId === 'ALL' ? null : Number(selectedBranchId);

      // grindhist는 이벤트(이력) 단위이므로 limit을 크게 주고 프론트에서 유저 기준으로 집계
      const response = await apiGetGrindHist(
        1,
        999999,
        branchIdParam,
        null,
        createdFrom,
        createdTo,
        null,
        user.accessToken,
      );

      const hist = Array.isArray(response?.data?.data) ? response.data.data : [];
      const sum = Number(response?.data?.sum_pet_cnt ?? 0);
      setSumPetCnt(sum);

      /** @type {Record<string, any>} */
      const byUserId = {};

      hist.forEach((grind) => {
        const userid = String(grind.userid ?? '').trim();
        if (!userid) return;

        const petCnt = Number(grind.pet_cnt ?? 0);
        const createdAt = grind.createdAt;
        const phone = grind.phone ?? '';
        const branchName = grind?.branch?.branch_name ?? '';

        if (!byUserId[userid]) {
          byUserId[userid] = {
            userid,
            phone,
            total_pet_cnt: 0,
            grind_count: 0,
            last_grind_at: createdAt,
            last_branch_name: branchName,
            branch_names: new Set(),
          };
        }

        if (branchName) byUserId[userid].branch_names.add(branchName);
        byUserId[userid].total_pet_cnt += petCnt;
        byUserId[userid].grind_count += 1;

        // 최근 분쇄일시 갱신
        if (createdAt && byUserId[userid].last_grind_at) {
          const prev = new Date(byUserId[userid].last_grind_at).getTime();
          const next = new Date(createdAt).getTime();
          if (next > prev) {
            byUserId[userid].last_grind_at = createdAt;
            byUserId[userid].last_branch_name = branchName;
            if (!byUserId[userid].phone && phone) byUserId[userid].phone = phone;
          }
        }
      });

      const aggregated = Object.values(byUserId).sort((a, b) => {
        const aTime = a.last_grind_at ? new Date(a.last_grind_at).getTime() : 0;
        const bTime = b.last_grind_at ? new Date(b.last_grind_at).getTime() : 0;
        return bTime - aTime;
      });

      setGrindRows(aggregated);
    } catch (e) {
      console.error('지점별 유저 조회 실패:', e);
      setErrorMessage('지점별 유저 목록을 불러오지 못했습니다.');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { Header: '지점', accessor: 'branch_name', align: 'left' },
      { Header: '사용자ID', accessor: 'userid', align: 'left' },
      { Header: 'Phone', accessor: 'phone', align: 'left' },
      { Header: '총 분쇄수량', accessor: 'total_pet_cnt', align: 'left' },
      { Header: '분쇄횟수', accessor: 'grind_count', align: 'left' },
      { Header: '최근 분쇄일시', accessor: 'last_grind_at', align: 'left' },
      { Header: '회원', accessor: 'member', align: 'center' },
    ],
    [],
  );

  const rows = useMemo(
    () =>
      grindRows.map((u) => ({
        branch_name: (() => {
          const branchCount = u.branch_names?.size ? u.branch_names.size : 0;
          if (branchCount <= 1) return u.last_branch_name || '-';
          return `${u.last_branch_name || '-'} 외 ${branchCount - 1}`;
        })(),
        userid: u.userid,
        phone: u.phone,
        total_pet_cnt: u.total_pet_cnt,
        grind_count: u.grind_count,
        last_grind_at: u.last_grind_at ? formatDateTime(u.last_grind_at) : '-',
        member: (
          <Button
            variant='contained'
            color='success'
            size='small'
            onClick={() =>
              navigate(`/members?page=1&searchcond=${encodeURIComponent(u.userid)}`)
            }
          >
            회원조회
          </Button>
        ),
      })),
    [grindRows, navigate],
  );

  const selectedBranchName = useMemo(() => {
    if (selectedBranchId === 'ALL') return '전체 지점';
    const b = branches.find((x) => String(x.id) === String(selectedBranchId));
    return b?.branch_name ?? '';
  }, [branches, selectedBranchId]);

  return (
    <DashboardLayout>
      <TopNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant='gradient'
                bgColor='info'
                borderRadius='lg'
                coloredShadow='info'
                display='flex'
                justifyContent='space-between'
                alignItems='center'
              >
                <MDTypography variant='h6' color='white'>
                  지점별 회원관리 (분쇄 이력 기준)
                </MDTypography>
                <MDTypography variant='h6' color='white'>
                  {selectedBranchName ? `${selectedBranchName} · ` : ''}
                  총Pet수: {sumPetCnt} · 유저수: {grindRows.length}
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label='지점 선택'
                      fullWidth
                      value={selectedBranchId}
                      onChange={(e) => setSelectedBranchId(e.target.value)}
                    >
                      <MenuItem value='ALL'>전체 지점</MenuItem>
                      {branches.map((b) => (
                        <MenuItem key={b.id} value={String(b.id)}>
                          {b.branch_name} (ID: {b.id})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label='분쇄일 From'
                      type='date'
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={createdFrom}
                      onChange={(e) => setCreatedFrom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label='분쇄일 To'
                      type='date'
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={createdTo}
                      onChange={(e) => setCreatedTo(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      variant='contained'
                      color='success'
                      fullWidth
                      onClick={fetchBranchUsers}
                      disabled={loading}
                      startIcon={<Icon>search</Icon>}
                    >
                      {loading ? '조회 중...' : '조회'}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity='error' sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BranchGrindUsers;

